import type { WebStudioActor, WebStudioCloudApiRequestByOperation } from "./generatedClient";

/**
 * Mutation envelope helpers (pure). Every Web Studio mutation carries the
 * same generated envelope fields — `actor`, `idempotencyKey`, and
 * `expectedState` — so the backend can attribute the action, deduplicate
 * retries, and reject writes against a scope the browser is no longer
 * looking at. The field names and shapes come from the generated mutation
 * request contracts (for example `mutateObservation`); this module never
 * invents envelope semantics of its own.
 */
type MutationRequestContract = WebStudioCloudApiRequestByOperation["mutateObservation"];

/**
 * Backend-owned optimistic-concurrency precondition. `targetDigest` names
 * the exact scope snapshot the browser mutated against; the backend fails
 * closed when the digest no longer matches.
 */
export type WebStudioMutationExpectedState = NonNullable<MutationRequestContract["expectedState"]>;

export interface WebStudioMutationEnvelope {
  actor: WebStudioActor;
  idempotencyKey: string;
  expectedState: WebStudioMutationExpectedState;
}

export interface BuildMutationEnvelopeInput {
  actorKind: string;
  actorId: string;
  expectedState: WebStudioMutationExpectedState;
  /** Injectable for tests; defaults to a fresh `crypto.randomUUID()`. */
  idempotencyKey?: string | undefined;
}

export function buildMutationEnvelope(
  input: BuildMutationEnvelopeInput,
): WebStudioMutationEnvelope {
  return {
    actor: {
      actorId: input.actorId,
      actorKind: input.actorKind,
    },
    idempotencyKey: input.idempotencyKey ?? crypto.randomUUID(),
    expectedState: input.expectedState,
  };
}
